import { Identify, createInstance } from '@amplitude/analytics-browser'

declare global {
  interface Window {
    AMPLITUDE_API_KEY?: string
    AMPLITUDE_HISTORICAL_API_KEY?: string
  }
}

// Module-level singletons — one per Amplitude project. Distinct instanceName keeps
// each instance's deviceId / session / userId storage isolated in localStorage.
export const amplitudeInstance = createInstance()
export const historicalInstance = createInstance()

const ANALYTICS_CONFIG = {
  autocapture: {
    attribution: true, // UTM / referrer attribution events
    pageViews: true, // SPA route changes + initial load
    sessions: true, // Session start / end events
    formInteractions: false, // Form starts + submits (opt-in when ready)
    fileDownloads: false, // Downloads of common file types (opt-in when ready)
    elementInteractions: false, // Click + change on instrumented elements
    frustrationInteractions: false, // Rage clicks, dead clicks
    pageUrlEnrichment: true, // Adds path / search to event props
    networkTracking: false, // XHR + fetch request events (opt-in when ready)
    webVitals: false, // CWV (LCP, INP, CLS) on page hide
  },
} as const

// `segment` naming kept on purpose (see `useSegment`).
export function initSegment(): void {
  const apiKey = window.AMPLITUDE_API_KEY
  const historicalKey = window.AMPLITUDE_HISTORICAL_API_KEY

  if (!apiKey && !historicalKey) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Amplitude] No API key set (AMPLITUDE_API_KEY / AMPLITUDE_HISTORICAL_API_KEY), skipping init'
    )
    return
  }

  if (apiKey) {
    amplitudeInstance.init(apiKey, {
      // serverZone: 'EU', // uncomment when switching to EU servers. Required for that.
      instanceName: 'amplitude',
      ...ANALYTICS_CONFIG,
    })
    amplitudeInstance.identify(new Identify().set('current_app_version', 'v1'))
  }

  if (historicalKey) {
    historicalInstance.init(historicalKey, {
      instanceName: 'historical',
      ...ANALYTICS_CONFIG,
    })
    historicalInstance.identify(new Identify().set('current_app_version', 'v1'))
  }

  // eslint-disable-next-line no-console
  console.info('[Amplitude] Initialized')
}
